import { useCallback, useEffect, useRef, useState } from 'react';
import type { AriaAttributes } from 'react';
import { FocusOn } from 'react-focus-on';

import { local } from '@farmersdog/utils';
import classNames from 'classnames';

import styles from './Sheet.module.css';

import { useViewport } from '../../hooks/useViewport';
import { Close } from '../../icons/Close';
import type { ColorName } from '../../styles/color';
import { getColor } from '../../styles/color';
import { ButtonBase } from '../ButtonBase';

type SheetBackgroundColor = ColorName | `#${string}`;

interface BaseSheetProps extends AriaAttributes {
  /** Optional unique identifier for the sheet */
  id?: string;
  /** Controls whether the sheet is open or closed */
  isOpen: boolean;
  /** Content to be displayed inside the sheet */
  children: React.ReactNode;
  /** Optional class name for the container */
  containerClassName?: string;
  /** Optional class name for the content */
  contentClassName?: string;
  /** Optional class name for the overlay */
  overlayClassName?: string;
  /**
   * Optional background color for the sheet content area. It's compatible with
   * both color names and hex codes.
   */
  contentBackgroundColor?: SheetBackgroundColor;
  /**
   * Optional width override for the sheet panel on desktop only (defaults to
   * 713px from CSS, mobile is always 100%)
   */
  width?: string;
  /**
   * Enable scroll-aware closing behavior on mobile. When true, user must scroll
   * to top before swipe-to-close works.
   *
   * @defaultValue false
   */
  scrollAwareClosing?: boolean;
  /**
   * Show a close button in the top right corner of the sheet.
   *
   * @defaultValue true when closable
   */
  showCloseButton?: boolean;
  /**
   * Optional class name applied to the close button element. Use to override
   * its size, background, or positioning (e.g., a white circle behind the
   * icon). Has no effect when {@link BaseSheetProps.showCloseButton} is false.
   */
  closeButtonClassName?: string;
  /**
   * Optional replacement for the default close-button icon. When provided, it
   * is rendered inside the close button instead of the standard `<Close>` icon.
   * Has no effect when {@link BaseSheetProps.showCloseButton} is false.
   */
  closeButtonContent?: React.ReactNode;
  /**
   * Optional localStorage key to track sheet open/close state. When provided,
   * the sheet will store 'true' when opened and 'false' when closed.
   *
   * @example LocalStorageKey "plans-page-drawer-open"
   */
  localStorageKey?: string;

  /**
   * Optional background color for the drag area. It's compatible with both
   * color names and hex codes.
   */
  dragAreaBackgroundColor?: SheetBackgroundColor;
}

interface ClosableSheetProps extends BaseSheetProps {
  /** Callback function called when the sheet should close */
  onClose: () => void;
  /**
   * Description for screen readers when the sheet cannot be closed - not
   * provided for closable sheets
   */
  lockedDescription?: never;
}

interface NonClosableSheetProps extends BaseSheetProps {
  /**
   * Callback function called when the sheet should close - not provided for
   * non-closable sheets
   */
  onClose?: never;
  /**
   * Description for screen readers when the sheet cannot be closed (required
   * when not closable)
   */
  lockedDescription: string;
}

export type SheetProps = ClosableSheetProps | NonClosableSheetProps;

// Matches the longest CSS transition duration across all themes (foundation
// theme uses 500ms `settle`; legacy themes use 300ms). Holding mounted state
// for the longer of the two is safe: legacy panels finish their slide at 300ms
// and remain off-screen for the extra 200ms before unmount.
const ANIMATION_DURATION = 500;
const SCROLL_DEBOUNCE_DURATION = 250; // milliseconds - debounce before allowing close after scrolling back up
const SCROLL_TO_CONTENT_TOP_THRESHOLD = 30; // pixels - threshold for considering user to be at the top of the content

/**
 * Sheet component - A slide-in panel that appears from the bottom on mobile and
 * from the right on desktop. Supports swipe-to-close gestures, keyboard
 * navigation, and focus trapping.
 *
 * @see https://corgi-x.tfd.engineering/components/sheet
 */
export const Sheet = (props: SheetProps) => {
  const {
    id,
    isOpen,
    onClose,
    children,
    lockedDescription,
    width,
    contentBackgroundColor = 'White',
    containerClassName,
    contentClassName,
    overlayClassName,
    scrollAwareClosing = false,
    showCloseButton = true,
    closeButtonClassName,
    closeButtonContent,
    localStorageKey,
    dragAreaBackgroundColor,
  } = props;

  const viewport = useViewport();
  const isMobile = !viewport.md;

  const handleStartY = useRef<number | null>(null);
  const contentStartY = useRef<number | null>(null);
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledDown = useRef(false);
  const isReadyToClose = useRef(false);
  const scrollDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset scroll state when sheet opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimatingOut(false);
      setHasBeenOpened(true);

      // Track open state in localStorage if key is provided
      if (localStorageKey) {
        local.setItem(localStorageKey, 'true');
      }

      hasScrolledDown.current = false;
      isReadyToClose.current = false;

      if (scrollDebounceTimeoutRef.current) {
        clearTimeout(scrollDebounceTimeoutRef.current);
        scrollDebounceTimeoutRef.current = null;
      }
    } else if (isVisible) {
      // Start closing animation
      setIsAnimatingOut(true);
      closeTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsAnimatingOut(false);
      }, ANIMATION_DURATION);
    }

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (scrollDebounceTimeoutRef.current) {
        clearTimeout(scrollDebounceTimeoutRef.current);
      }
    };
  }, [isOpen, isVisible, localStorageKey]);

  const handleClose = () => {
    if (onClose && !isAnimatingOut) {
      // Track close state in localStorage if key is provided
      if (localStorageKey) {
        local.setItem(localStorageKey, 'false');
      }
      onClose();
    }
  };

  // Handle touch on the drag handle area (top of sheet on mobile) - direct swipe to close
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !scrollAwareClosing) {
      return;
    }
    handleStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !scrollAwareClosing || handleStartY.current === null) {
      return;
    }

    const endY = e.changedTouches[0].clientY;
    const deltaY = endY - handleStartY.current;

    // Swipe down on handle area closes sheet
    if (deltaY > 50) {
      handleClose();
    }

    handleStartY.current = null;
  };

  // Handle touch on content area - scroll-aware closing with debounce
  const handleContentTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) {
      return;
    }

    // Simple swipe-to-close when scroll-aware closing is disabled
    if (!scrollAwareClosing) {
      contentStartY.current = e.touches[0].clientY;
      return;
    }

    // Scroll-aware behavior
    contentStartY.current = e.touches[0].clientY;
  };

  // NOTE: handler fires repeatedly while scrolling through sheet content.
  const handleContentTouchMove = useCallback(() => {
    if (!isMobile || !contentRef.current || !scrollAwareClosing) {
      return;
    }

    const scrollTop = contentRef.current.scrollTop;

    // Track if user has scrolled down into content
    if (scrollTop > 0) {
      hasScrolledDown.current = true;
      isReadyToClose.current = false;
      if (scrollDebounceTimeoutRef.current) {
        clearTimeout(scrollDebounceTimeoutRef.current);
        scrollDebounceTimeoutRef.current = null;
      }
    }

    if (hasScrolledDown.current && !isReadyToClose.current) {
      if (!scrollDebounceTimeoutRef.current) {
        scrollDebounceTimeoutRef.current = setTimeout(() => {
          isReadyToClose.current = true;
          scrollDebounceTimeoutRef.current = null;
        }, SCROLL_DEBOUNCE_DURATION);
      }
    }
  }, [isMobile, scrollAwareClosing]);

  const handleContentTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || contentStartY.current === null) {
      return;
    }

    const endY = e.changedTouches[0].clientY;
    const deltaY = endY - contentStartY.current;

    if (!scrollAwareClosing) {
      // Simple swipe-to-close behavior
      if (deltaY > 50) {
        handleClose();
      }
    } else if (contentRef.current) {
      // Scroll-aware behavior
      const scrollTop = contentRef.current.scrollTop;
      const canClose =
        scrollTop === 0 && (!hasScrolledDown.current || isReadyToClose.current);

      if (canClose && deltaY > SCROLL_TO_CONTENT_TOP_THRESHOLD) {
        handleClose();
      }
    }

    contentStartY.current = null;
  };

  return (
    <FocusOn enabled={isOpen} focusLock scrollLock onEscapeKey={handleClose}>
      <div
        data-testid="sheet-container"
        className={classNames(styles.container, containerClassName, {
          [styles.open]: isVisible,
        })}
      >
        {onClose ? (
          <button
            onClick={handleClose}
            type="button"
            className={classNames(styles.overlay, overlayClassName, {
              [styles.open]: isOpen && !isAnimatingOut,
            })}
            aria-label="Close dialog by clicking outside"
          />
        ) : (
          <div
            className={classNames(styles.overlay, overlayClassName, {
              [styles.open]: isOpen && !isAnimatingOut,
            })}
            role="presentation"
          />
        )}
        <div
          id={id}
          aria-modal={true}
          aria-hidden={!isOpen}
          aria-labelledby={props['aria-labelledby']}
          aria-description={onClose ? undefined : lockedDescription}
          className={classNames(styles.slidingPanel, {
            [styles.open]: isOpen && !isAnimatingOut,
          })}
          style={width && !isMobile ? { width } : undefined}
        >
          {/* Mobile drag area - tapping and swiping down here will close Sheet */}
          {isMobile && onClose && scrollAwareClosing && (
            <div
              data-testid="sheet-drag-area"
              className={styles.dragArea}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              aria-hidden="true"
              style={
                dragAreaBackgroundColor
                  ? { backgroundColor: getColor(dragAreaBackgroundColor) }
                  : undefined
              }
            />
          )}
          {!isMobile && onClose && (
            <div
              data-testid="sheet-header-area"
              className={styles.headerArea}
              aria-hidden="true"
              style={
                dragAreaBackgroundColor
                  ? { backgroundColor: getColor(dragAreaBackgroundColor) }
                  : undefined
              }
            />
          )}
          {onClose && showCloseButton && (
            <ButtonBase
              type="button"
              onClick={handleClose}
              className={classNames(styles.closeButton, closeButtonClassName)}
              aria-label="Close dialog"
            >
              {closeButtonContent ?? (
                <Close aria-hidden baseColor="Charcoal3" size={36} />
              )}
            </ButtonBase>
          )}
          <div
            ref={contentRef}
            className={classNames(styles.content, contentClassName)}
            style={
              contentBackgroundColor
                ? { backgroundColor: getColor(contentBackgroundColor) }
                : undefined
            }
            onTouchStart={handleContentTouchStart}
            onTouchEnd={handleContentTouchEnd}
            onTouchMove={
              scrollAwareClosing ? handleContentTouchMove : undefined
            }
          >
            {hasBeenOpened && children}
          </div>
        </div>
      </div>
    </FocusOn>
  );
};
